// Technical documentation in the Platform Admin reference section.
import MarkdownIt from 'markdown-it';
import { Markup } from '../../components/Markup';
import { raw } from '../../lib/dom';
import type { PageContext, PageDefinition } from '../../lib/types';
import { setQuery } from '../../lib/router';
import '../../lib/styles/knowledge.css';
import environments from '../../../../../docs/environments.md?raw';
import serverRuns from '../../../../../docs/technical/canvas-server-runs.md?raw';
import openrouter from '../../../../../docs/technical/openrouter.md?raw';
import architecture from '../../../../../docs/technical/01-architecture.md?raw';
import canvas from '../../../../../docs/technical/02-canvas.md?raw';
import integrations from '../../../../../docs/technical/03-integrations.md?raw';
import repository from '../../../../../docs/technical/04-repository.md?raw';
import maintenance from '../../../../../docs/technical/05-maintenance.md?raw';
import supabaseCloud from '../../../../../docs/supabase-cloud.md?raw';
import nodeCatalog from '../../../../../packages/canvas-runtime/docs/NODE_CATALOG.md?raw';
import redis from '../../../../../docs/redis.md?raw';
import generation from '../../../../../docs/generation.md?raw';
import definitionOfDone from '../../../../../docs/dod.md?raw';
import databaseCatalog from '../../../../../docs/technical/generation-catalog.md?raw';
import remotion from '../../../../../docs/technical/remotion.md?raw';
import flowApi from '../../../../../docs/technical/flow-api.md?raw';

type KnowledgeDoc = { slug: string; title: string; summary: string; category: string; source: string; markdown: string };

export const documents: KnowledgeDoc[] = [
  { slug: 'environments', title: 'Environments and releases', summary: 'Development, production, databases, and deployment.', category: 'Operations', source: 'docs/environments.md', markdown: environments },
  { slug: 'server-runs', title: 'Server executions', summary: 'Background execution, checkpoints, and Canvas progress.', category: 'Canvas', source: 'docs/technical/canvas-server-runs.md', markdown: serverRuns },
  { slug: 'openrouter', title: 'OpenRouter', summary: 'Text models, provider setup, and free models.', category: 'Architecture', source: 'docs/technical/openrouter.md', markdown: openrouter },
  { slug: 'architecture', title: 'Architecture and data', summary: 'Current components, entities, storage, and access.', category: 'Architecture', source: 'docs/technical/01-architecture.md', markdown: architecture },
  { slug: 'canvas', title: 'Canvas', summary: 'The mounted editor, graph persistence, and runs.', category: 'Canvas', source: 'docs/technical/02-canvas.md', markdown: canvas },
  { slug: 'canvas-node-catalog', title: 'BnB Nodes catalog', summary: 'Canvas nodes and models, their settings, and output types.', category: 'Canvas', source: 'packages/canvas-runtime/docs/NODE_CATALOG.md', markdown: nodeCatalog },
  { slug: 'integrations', title: 'Integrations', summary: 'Available app links and internal API endpoints.', category: 'Architecture', source: 'docs/technical/03-integrations.md', markdown: integrations },
  { slug: 'repository', title: 'Repository map', summary: 'Where the application and Canvas code lives.', category: 'Development', source: 'docs/technical/04-repository.md', markdown: repository },
  { slug: 'maintenance', title: 'Development and maintenance', summary: 'Setup, checks, and release workflow.', category: 'Development', source: 'docs/technical/05-maintenance.md', markdown: maintenance },
  { slug: 'supabase-cloud', title: 'Production architecture', summary: 'VPS, Supabase Cloud, R2, and releases.', category: 'Operations', source: 'docs/supabase-cloud.md', markdown: supabaseCloud },
  { slug: 'redis', title: 'Redis', summary: 'The queue and Redis connection setup.', category: 'Operations', source: 'docs/redis.md', markdown: redis },
  { slug: 'generation', title: 'Generation limits', summary: 'Current execution path and known failure modes.', category: 'Operations', source: 'docs/generation.md', markdown: generation },
  { slug: 'definition-of-done', title: 'Definition of done', summary: 'Criteria for completing work on the project.', category: 'Development', source: 'docs/dod.md', markdown: definitionOfDone },
  { slug: 'database-catalog', title: 'Database Catalog', summary: 'Import, stable identities, availability and pinned run settings.', category: 'Architecture', source: 'docs/technical/generation-catalog.md', markdown: databaseCatalog },
  { slug: 'remotion', title: 'Remotion Templates', summary: 'HTML cards, element animations, template snapshots and PNG/MP4 rendering.', category: 'Canvas', source: 'docs/technical/remotion.md', markdown: remotion },
  { slug: 'flow-api', title: 'Flow API', summary: 'Bearer integration, input schemas, idempotency and result retrieval.', category: 'Architecture', source: 'docs/technical/flow-api.md', markdown: flowApi },
];

const markdown = new MarkdownIt({ html: false, linkify: true, typographer: true });

function slugifyHeading(value: string) {
  return value.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '');
}

export function knowledgeLink(href: string, sourcePath: string) {
  if (/^(?:[a-z][a-z\d+.-]*:|\/\/|\/)/i.test(href)) return href;
  const target = new URL(href, `https://documentation.invalid/${sourcePath}`);
  const source = decodeURIComponent(target.pathname.slice(1));
  const selected = documents.find(document => document.source === source);
  if (selected) {
    const query = new URLSearchParams({ doc: selected.slug });
    if (target.hash) query.set('anchor', decodeURIComponent(target.hash.slice(1)));
    return `#/admin/knowledge?${query}`;
  }
  // Additional repository references remain readable without creating app routes.
  if (source.endsWith('.md')) return `https://github.com/bots-n-bones/nodes_v3/blob/production/${target.pathname.slice(1)}${target.hash}`;
  return href;
}

export function parseKnowledgeMarkdown(source: string, sourcePath?: string) {
  const anchorByLine = new Map<number, string>();
  const anchorPattern = /<a\s+id=["']([a-z0-9_-]+)["']\s*><\/a>/gi;
  for (const match of source.matchAll(anchorPattern)) {
    const anchorLine = source.slice(0, match.index).split('\n').length - 1;
    const lines = source.split('\n');
    for (let line = anchorLine + 1; line < lines.length; line++) {
      if (!lines[line].trim()) continue;
      if (/^#{1,6}\s/.test(lines[line])) anchorByLine.set(line, match[1]);
      break;
    }
  }
  const safeSource = source.replace(anchorPattern, '');
  const tokens = markdown.parse(safeSource, {});
  if (sourcePath) {
    const rewriteLinks = (items: typeof tokens) => {
      for (const token of items) {
        if (token.type === 'link_open') {
          const href = token.attrGet('href');
          if (typeof href === 'string') token.attrSet('href', knowledgeLink(href, sourcePath));
        }
        if (token.children) rewriteLinks(token.children);
      }
    };
    rewriteLinks(tokens);
  }
  const headings: Array<{ id: string; title: string; level: number }> = [];
  for (let index = 0; index < tokens.length; index++) {
    const token = tokens[index];
    if (token.type !== 'heading_open') continue;
    const inline = tokens[index + 1];
    const title = inline?.content || '';
    const id = anchorByLine.get(token.map?.[0] ?? -1) || slugifyHeading(title);
    token.attrSet('id', id);
    headings.push({ id, title, level: Number(token.tag.slice(1)) });
  }
  const html = markdown.renderer.render(tokens, markdown.options, {});
  return { html, headings };
}

export function renderMarkdown(source: string) { return parseKnowledgeMarkdown(source).html; }

const categories = ['Architecture', 'Canvas', 'Operations', 'Development'];

export function render(ctx: PageContext): PageDefinition {
  const selected = documents.find(document => document.slug === ctx.query.doc);
  const search = (ctx.query.q || '').trim().toLocaleLowerCase();
  const visible = documents.filter(document => !search || [document.title, document.summary, document.category, document.source].some(value => value.toLocaleLowerCase().includes(search)));

  if (selected) {
    const parsed = parseKnowledgeMarkdown(selected.markdown, selected.source);
    return {
      title: selected.title,
      crumbs: [['Technical Knowledge', '#/admin/knowledge'], [selected.title]],
      mount: () => {
        if (ctx.query.anchor) requestAnimationFrame(() => document.getElementById(ctx.query.anchor)?.scrollIntoView());
      },
      content: <div className="knowledge-page page">
        <a className="btn ghost sm knowledge-back" href="#/admin/knowledge">← All documentation</a>
        <div className="knowledge-reader-layout">
          <article className="knowledge-reader">
            <header className="knowledge-reader-head">
              <span className="knowledge-kicker">{selected.category}</span>
              <h1>{selected.title}</h1>
              <p>{selected.summary}</p>
              <div className="knowledge-source"><span>Markdown source</span><code>{selected.source}</code></div>
            </header>
            <div className="knowledge-prose"><Markup value={raw(parsed.html)} /></div>
          </article>
          <aside className="knowledge-toc" aria-label="On this page">
            <span className="knowledge-kicker">On this page</span>
            {parsed.headings.length ? parsed.headings.map((heading, index) => <a key={`${heading.id}-${index}`} className={heading.level > 2 ? 'nested' : ''} href={`#/admin/knowledge?doc=${encodeURIComponent(selected.slug)}&anchor=${encodeURIComponent(heading.id)}`}>{heading.title}</a>) : <span className="muted">No headings</span>}
          </aside>
        </div>
      </div>,
    };
  }

  return {
    title: 'Technical Knowledge',
    content: <div className="knowledge-page page">
      <section className="knowledge-hero">
        <div className="knowledge-hero-copy">
          <span className="knowledge-kicker">PROJECT DOCUMENTATION</span>
          <h1>Technical Knowledge</h1>
          <p>Guides to Canvas, generation, environments, and project maintenance.</p>
          <a className="btn knowledge-featured-link" href="#/admin/knowledge?doc=architecture">Read architecture →</a>
        </div>

      </section>

      <div className="knowledge-toolbar">
        <div><span className="knowledge-kicker">DOCUMENT LIBRARY</span><p>{visible.length} documents in the project</p></div>
        <label className="knowledge-search" data-local-tabs><span className="sr-only">Search documentation</span><input type="search" data-filter="q" value={ctx.query.q || ''} onChange={event => { setQuery({ q: event.target.value || null }); ctx.rerender(); }} placeholder="Find a document…" /></label>
      </div>

      {visible.length ? categories.map(category => {
        const items = visible.filter(document => document.category === category);
        if (!items.length) return null;
        return <section className="knowledge-section" key={category}>
          <div className="knowledge-section-head"><h2>{category}</h2><span>{String(items.length).padStart(2, '0')}</span></div>
          <div className="knowledge-grid">{items.map(document => <a className="knowledge-card" href={`#/admin/knowledge?doc=${encodeURIComponent(document.slug)}`} key={document.slug}>
            <div className="knowledge-card-top"><span className="knowledge-doc-icon">MD</span><span className="knowledge-kicker">{document.category}</span><span className="knowledge-arrow">↗</span></div>
            <h3>{document.title}</h3>
            <p>{document.summary}</p>
            <div className="knowledge-card-foot"><span>{document.source}</span><span>Markdown</span></div>
          </a>)}</div>
        </section>;
      }) : <div className="knowledge-empty">No documents found. Try a different search.</div>}
    </div>,
  };
}
